import { z } from 'zod';
import { baseMessageSchema, createMessageId } from './_base';

export enum AdminMessageType {
  ADMIN_ACTIVE_HOSTS_RESULT = 'ADMIN_ACTIVE_HOSTS_RESULT',
  ADMIN_OPENED_SESSIONS_RESULT = 'ADMIN_OPENED_SESSIONS_RESULT',
  ADMIN_FETCH_OPENED_SESSIONS = 'ADMIN_FETCH_OPENED_SESSIONS',
  ADMIN_FETCH_ONLINE_HOSTS = 'ADMIN_FETCH_ONLINE_HOSTS',
  ADMIN_FETCH_ONLINE_HOSTS_PAGINATED = 'ADMIN_FETCH_ONLINE_HOSTS_PAGINATED',
  ADMIN_ACTIVE_HOSTS_PAGINATED_RESULT = 'ADMIN_ACTIVE_HOSTS_PAGINATED_RESULT',
  ADMIN_FETCH_SINGLE_HOST = 'ADMIN_FETCH_SINGLE_HOST',
  ADMIN_SINGLE_HOST_RESULT = 'ADMIN_SINGLE_HOST_RESULT',
  ADMIN_FETCH_INVITATIONS_PAGINATED = 'ADMIN_FETCH_INVITATIONS_PAGINATED',
  ADMIN_INVITATIONS_PAGINATED_RESULT = 'ADMIN_INVITATIONS_PAGINATED_RESULT',
  ADMIN_SUBSCRIBE_ONLINE_HOSTS = 'ADMIN_SUBSCRIBE_ONLINE_HOSTS',
  ADMIN_KICK_PLAYER = 'ADMIN_KICK_PLAYER',
  ADMIN_KICK_PLAYER_RESULT = 'ADMIN_KICK_PLAYER_RESULT',
  ADMIN_FETCH_HOST_PLAYERS = 'ADMIN_FETCH_HOST_PLAYERS',
  ADMIN_HOST_PLAYERS_RESULT = 'ADMIN_HOST_PLAYERS_RESULT',
  ADMIN_TOGGLE_HOST_MATCHMAKING = 'ADMIN_TOGGLE_HOST_MATCHMAKING',
  ADMIN_TOGGLE_HOST_MATCHMAKING_RESULT = 'ADMIN_TOGGLE_HOST_MATCHMAKING_RESULT',
  ADMIN_INVALIDATE_HOST_SESSION = 'ADMIN_INVALIDATE_HOST_SESSION',
  ADMIN_INVALIDATE_HOST_SESSION_RESULT = 'ADMIN_INVALIDATE_HOST_SESSION_RESULT',
  ADMIN_SWITCH_HOST_CAMPAIGN = 'ADMIN_SWITCH_HOST_CAMPAIGN',
  ADMIN_SWITCH_HOST_CAMPAIGN_RESULT = 'ADMIN_SWITCH_HOST_CAMPAIGN_RESULT',
  ADMIN_SESSION_REQUEST = 'ADMIN_SESSION_REQUEST',
  ADMIN_SESSION_APPROVED = 'ADMIN_SESSION_APPROVED',
  ADMIN_SESSION_DENIED = 'ADMIN_SESSION_DENIED',
  ADMIN_SESSION_END = 'ADMIN_SESSION_END',
  ADMIN_SESSION_TIMEOUT_WARNING = 'ADMIN_SESSION_TIMEOUT_WARNING',
  ADMIN_SESSION_PREPARE = 'ADMIN_SESSION_PREPARE',
  ADMIN_FETCH_CONNECTION_HISTORY = 'ADMIN_FETCH_CONNECTION_HISTORY',
  ADMIN_CONNECTION_HISTORY_RESULT = 'ADMIN_CONNECTION_HISTORY_RESULT',
  ADMIN_LIVEOPS_SUBSCRIBE = 'ADMIN_LIVEOPS_SUBSCRIBE',
  ADMIN_LIVEOPS_UNSUBSCRIBE = 'ADMIN_LIVEOPS_UNSUBSCRIBE',
  ADMIN_LIVEOPS_SNAPSHOT = 'ADMIN_LIVEOPS_SNAPSHOT',
  ADMIN_LIVEOPS_DELTA = 'ADMIN_LIVEOPS_DELTA',
  ADMIN_SCREENSHOT_GRAB_REQUEST = 'ADMIN_SCREENSHOT_GRAB_REQUEST',
  ADMIN_SCREENSHOT_GRAB_RESULT = 'ADMIN_SCREENSHOT_GRAB_RESULT',
  ADMIN_SCREENSHOT_GRAB_ERROR = 'ADMIN_SCREENSHOT_GRAB_ERROR',
  ADMIN_SCHEDULED_SCREENSHOT_GRAB_REQUEST = 'ADMIN_SCHEDULED_SCREENSHOT_GRAB_REQUEST',
  ADMIN_SCHEDULED_SCREENSHOT_GRAB_RESULT = 'ADMIN_SCHEDULED_SCREENSHOT_GRAB_RESULT',
  ADMIN_SCHEDULED_SCREENSHOT_GRAB_ERROR = 'ADMIN_SCHEDULED_SCREENSHOT_GRAB_ERROR',
}

const adminActiveHostsResultPayloadSchema = z.object({
  data: z.array(z.any()),
});

const adminOpenedSessionsResultPayloadSchema = z.object({
  sessions: z.array(z.any()),
});

const adminFetchOpenedSessionsPayloadSchema = z.object({});

const adminFetchActiveHostsPayloadSchema = z.object({});
const adminFetchActiveHostsPaginatedPayloadSchema = z.object({
  cursor: z.string(),
  count: z.number(),
});
const adminActiveHostsPaginatedResultPayloadSchema = z.object({
  data: z.array(z.any()),
  cursor: z.string(),
  hasMore: z.boolean(),
});
const adminFetchSingleHostPayloadSchema = z.object({
  machineId: z.string(),
});
const adminSingleHostResultPayloadSchema = z.object({
  machineId: z.string(),
  host: z.any().nullable(),
});
const adminFetchInvitationsPaginatedPayloadSchema = z.object({
  cursor: z.string(),
  count: z.number(),
  filters: z
    .object({
      searchText: z.string().optional(),
      type: z.enum(['lobby', 'cafe']).optional(),
      queueStatus: z.string().optional(),
    })
    .optional(),
});
const adminInvitationsPaginatedResultPayloadSchema = z.object({
  invitations: z.array(z.any()),
  cursor: z.string(),
  hasMore: z.boolean(),
});
const adminSubscribeActiveHostsPayloadSchema = z.object({});

const adminKickPlayerPayloadSchema = z.object({
  machineId: z.string(),
  playerId: z.string(),
  kickType: z.enum(['soft', 'hard']),
  reason: z.string().optional(),
});

const adminKickPlayerResultPayloadSchema = z.object({
  success: z.boolean(),
  message: z.string().optional(),
  error: z.string().optional(),
  kickType: z.enum(['soft', 'hard']),
  playerId: z.string(),
  machineId: z.string(),
});

const adminFetchHostPlayersPayloadSchema = z.object({
  machineId: z.string(),
});

const adminHostPlayersResultPayloadSchema = z.object({
  machineId: z.string(),
  players: z.array(
    z.object({
      playerId: z.string(),
      joinTime: z.number(),
    }),
  ),
});

const adminToggleHostMatchmakingPayloadSchema = z.object({
  machineId: z.string(),
  enabled: z.boolean(),
});

const adminToggleHostMatchmakingResultPayloadSchema = z.object({
  success: z.boolean(),
  machineId: z.string(),
  enabled: z.boolean(),
  message: z.string().optional(),
  error: z.string().optional(),
});

const adminInvalidateHostSessionPayloadSchema = z.object({
  machineId: z.string(),
  reason: z.string().optional(),
});

const adminInvalidateHostSessionResultPayloadSchema = z.object({
  success: z.boolean(),
  machineId: z.string(),
  message: z.string().optional(),
  error: z.string().optional(),
});

const adminSwitchHostCampaignPayloadSchema = z.object({
  machineId: z.string(),
  campaignId: z.string(),
});

const adminSwitchHostCampaignResultPayloadSchema = z.object({
  success: z.boolean(),
  machineId: z.string(),
  campaignId: z.string(),
  message: z.string().optional(),
  error: z.string().optional(),
});

// --- Admin Session Messages ---

export const macroAuthoringSessionContextSchema = z.object({
  purpose: z.literal('macroRecorder'),
  campaignId: z.string().trim().min(1).max(128),
});
export type MacroAuthoringSessionContext = z.infer<typeof macroAuthoringSessionContextSchema>;

const adminSessionRequestPayloadSchema = z.object({
  machineId: z.string(),
  force: z.boolean().optional(),
  authoring: macroAuthoringSessionContextSchema.optional(),
});

const adminSessionApprovedPayloadSchema = z.object({
  machineId: z.string(),
  sessionId: z.string(),
  hostBroadcastId: z.string(),
  playerPeerId: z.string().optional(),
});

const adminSessionDeniedPayloadSchema = z.object({
  machineId: z.string(),
  reason: z.enum(['not_guest_mode', 'admin_session_active', 'unauthorized', 'machine_offline', 'insufficient_permissions']),
});

export const adminSessionDisconnectReasonSchema = z.enum(['admin_exit', 'timeout', 'unexpected']);
export type AdminSessionDisconnectReason = z.infer<typeof adminSessionDisconnectReasonSchema>;

export const adminSessionExitActionSchema = z.enum(['reboot', 'restart_game', 'keep_game', 'exit_nonsole']);
export type AdminSessionExitAction = z.infer<typeof adminSessionExitActionSchema>;

const adminSessionEndPayloadSchema = z.object({
  sessionId: z.string(),
  machineId: z.string(),
  reason: adminSessionDisconnectReasonSchema,
  reboot: z.boolean().optional(),
  exitAction: adminSessionExitActionSchema.optional(),
  /** Cancel only the exact short-lived reservation; never exit an established Host session. */
  cancelPendingApproval: z.boolean().optional(),
});

const adminSessionTimeoutWarningPayloadSchema = z.object({
  sessionId: z.string(),
  remainingSeconds: z.number(),
});

const adminSessionPreparePayloadSchema = z.object({
  machineId: z.string(),
});

const adminFetchConnectionHistoryPayloadSchema = z.object({
  cursor: z.string(),
  count: z.number(),
  filters: z
    .object({
      machineId: z.string().optional(),
      playerId: z.string().optional(),
      startTime: z.number().optional(),
      endTime: z.number().optional(),
    })
    .optional(),
});

const adminConnectionHistoryResultPayloadSchema = z.object({
  records: z.array(
    z.object({
      id: z.string(),
      machineId: z.string(),
      machineName: z.string().optional(),
      campaignId: z.string().optional(),
      playerId: z.string(),
      invitationId: z.string(),
      sessionId: z.string().optional(),
      connectionType: z.enum(['cafe', 'lobby']),
      timestamp: z.number(),
    }),
  ),
  nextCursor: z.string().nullable(),
  hasMore: z.boolean(),
});

// --- LiveOps Messages ---

const adminLiveOpsSubscribePayloadSchema = z.object({
  campaignId: z.string(),
});

const adminLiveOpsUnsubscribePayloadSchema = z.object({
  campaignId: z.string(),
});

const adminLiveOpsSnapshotPayloadSchema = z.object({
  campaignId: z.string(),
  timestamp: z.number(),
  ccu: z.number(),
  ccuHistory: z.array(z.number()),
  matchmaking: z.object({
    started: z.number(),
    succeeded: z.number(),
    failed: z.number(),
  }),
  successRateHistory: z.array(z.number()),
  failureRateHistory: z.array(z.number()),
  saturation: z.object({
    usedHosts: z.number(),
    totalHosts: z.number(),
  }),
  saturationHistory: z.array(z.number()),
  funnel: z.record(z.number()),
  connectionTime: z.object({
    bins: z.record(z.number()),
    p50: z.number(),
    p95: z.number(),
  }),
  errors: z.record(z.number()),
});

export type LiveOpsSnapshotData = z.infer<typeof adminLiveOpsSnapshotPayloadSchema>;

const adminLiveOpsDeltaPayloadSchema = z.object({
  campaignId: z.string(),
  timestamp: z.number(),
  changes: z.record(z.unknown()),
});

// --- Screenshot Grab Messages ---

const adminScreenshotGrabRequestPayloadSchema = z.object({
  machineId: z.string(),
});

const adminScreenshotGrabResultPayloadSchema = z.object({
  machineId: z.string(),
  imageUrl: z.string().url(),
  timestamp: z.number(),
  sizeBytes: z.number(),
  expiresAt: z.number(),
});

const adminScreenshotGrabErrorPayloadSchema = z.object({
  machineId: z.string(),
  error: z.string(),
});

export const SCREENSHOT_CONSTANTS = {
  HOST_SCREENSHOT_PATH: 'C:\\ProgramData\\Playcast\\screenshots\\macro_latest.png',
  HOST_SCHEDULED_SCREENSHOT_PATH: 'C:\\ProgramData\\Playcast\\screenshots\\scheduled_latest.png',
  S3_PREFIX: 'client-screenshots',
  PRESIGNED_PUT_EXPIRY_SECONDS: 300,
  PRESIGNED_GET_EXPIRY_SECONDS: 86400,
  REDIS_LOCK_KEY_PREFIX: 'screenshot-grab',
  REDIS_LOCK_TTL_SECONDS: 60,
} as const;

const adminActiveHostsResultSchema = baseMessageSchema
  .extend({
    type: z.literal(AdminMessageType.ADMIN_ACTIVE_HOSTS_RESULT),
  })
  .merge(adminActiveHostsResultPayloadSchema);

const adminOpenedSessionsResultSchema = baseMessageSchema
  .extend({
    type: z.literal(AdminMessageType.ADMIN_OPENED_SESSIONS_RESULT),
  })
  .merge(adminOpenedSessionsResultPayloadSchema);

const adminFetchOpenedSessionsSchema = baseMessageSchema
  .extend({
    type: z.literal(AdminMessageType.ADMIN_FETCH_OPENED_SESSIONS),
  })
  .merge(adminFetchOpenedSessionsPayloadSchema);

const adminFetchActiveHostsSchema = baseMessageSchema
  .extend({
    type: z.literal(AdminMessageType.ADMIN_FETCH_ONLINE_HOSTS),
  })
  .merge(adminFetchActiveHostsPayloadSchema);

const adminFetchActiveHostsPaginatedSchema = baseMessageSchema
  .extend({
    type: z.literal(AdminMessageType.ADMIN_FETCH_ONLINE_HOSTS_PAGINATED),
  })
  .merge(adminFetchActiveHostsPaginatedPayloadSchema);

const adminActiveHostsPaginatedResultSchema = baseMessageSchema
  .extend({
    type: z.literal(AdminMessageType.ADMIN_ACTIVE_HOSTS_PAGINATED_RESULT),
  })
  .merge(adminActiveHostsPaginatedResultPayloadSchema);

const adminFetchSingleHostSchema = baseMessageSchema
  .extend({
    type: z.literal(AdminMessageType.ADMIN_FETCH_SINGLE_HOST),
  })
  .merge(adminFetchSingleHostPayloadSchema);

const adminSingleHostResultSchema = baseMessageSchema
  .extend({
    type: z.literal(AdminMessageType.ADMIN_SINGLE_HOST_RESULT),
  })
  .merge(adminSingleHostResultPayloadSchema);

const adminFetchInvitationsPaginatedSchema = baseMessageSchema
  .extend({
    type: z.literal(AdminMessageType.ADMIN_FETCH_INVITATIONS_PAGINATED),
  })
  .merge(adminFetchInvitationsPaginatedPayloadSchema);

const adminInvitationsPaginatedResultSchema = baseMessageSchema
  .extend({
    type: z.literal(AdminMessageType.ADMIN_INVITATIONS_PAGINATED_RESULT),
  })
  .merge(adminInvitationsPaginatedResultPayloadSchema);

const adminSubscribeActiveHostsSchema = baseMessageSchema
  .extend({
    type: z.literal(AdminMessageType.ADMIN_SUBSCRIBE_ONLINE_HOSTS),
  })
  .merge(adminSubscribeActiveHostsPayloadSchema);

const adminKickPlayerSchema = baseMessageSchema
  .extend({
    type: z.literal(AdminMessageType.ADMIN_KICK_PLAYER),
  })
  .merge(adminKickPlayerPayloadSchema);

const adminKickPlayerResultSchema = baseMessageSchema
  .extend({
    type: z.literal(AdminMessageType.ADMIN_KICK_PLAYER_RESULT),
  })
  .merge(adminKickPlayerResultPayloadSchema);

const adminFetchHostPlayersSchema = baseMessageSchema
  .extend({
    type: z.literal(AdminMessageType.ADMIN_FETCH_HOST_PLAYERS),
  })
  .merge(adminFetchHostPlayersPayloadSchema);

const adminHostPlayersResultSchema = baseMessageSchema
  .extend({
    type: z.literal(AdminMessageType.ADMIN_HOST_PLAYERS_RESULT),
  })
  .merge(adminHostPlayersResultPayloadSchema);

const adminToggleHostMatchmakingSchema = baseMessageSchema
  .extend({
    type: z.literal(AdminMessageType.ADMIN_TOGGLE_HOST_MATCHMAKING),
  })
  .merge(adminToggleHostMatchmakingPayloadSchema);

const adminToggleHostMatchmakingResultSchema = baseMessageSchema
  .extend({
    type: z.literal(AdminMessageType.ADMIN_TOGGLE_HOST_MATCHMAKING_RESULT),
  })
  .merge(adminToggleHostMatchmakingResultPayloadSchema);

const adminInvalidateHostSessionSchema = baseMessageSchema
  .extend({
    type: z.literal(AdminMessageType.ADMIN_INVALIDATE_HOST_SESSION),
  })
  .merge(adminInvalidateHostSessionPayloadSchema);

const adminInvalidateHostSessionResultSchema = baseMessageSchema
  .extend({
    type: z.literal(AdminMessageType.ADMIN_INVALIDATE_HOST_SESSION_RESULT),
  })
  .merge(adminInvalidateHostSessionResultPayloadSchema);

const adminSwitchHostCampaignSchema = baseMessageSchema
  .extend({
    type: z.literal(AdminMessageType.ADMIN_SWITCH_HOST_CAMPAIGN),
  })
  .merge(adminSwitchHostCampaignPayloadSchema);

const adminSwitchHostCampaignResultSchema = baseMessageSchema
  .extend({
    type: z.literal(AdminMessageType.ADMIN_SWITCH_HOST_CAMPAIGN_RESULT),
  })
  .merge(adminSwitchHostCampaignResultPayloadSchema);

const adminSessionRequestSchema = baseMessageSchema
  .extend({
    type: z.literal(AdminMessageType.ADMIN_SESSION_REQUEST),
  })
  .merge(adminSessionRequestPayloadSchema);

const adminSessionApprovedSchema = baseMessageSchema
  .extend({
    type: z.literal(AdminMessageType.ADMIN_SESSION_APPROVED),
  })
  .merge(adminSessionApprovedPayloadSchema);

const adminSessionDeniedSchema = baseMessageSchema
  .extend({
    type: z.literal(AdminMessageType.ADMIN_SESSION_DENIED),
  })
  .merge(adminSessionDeniedPayloadSchema);

const adminSessionEndSchema = baseMessageSchema
  .extend({
    type: z.literal(AdminMessageType.ADMIN_SESSION_END),
  })
  .merge(adminSessionEndPayloadSchema);

const adminSessionTimeoutWarningSchema = baseMessageSchema
  .extend({
    type: z.literal(AdminMessageType.ADMIN_SESSION_TIMEOUT_WARNING),
  })
  .merge(adminSessionTimeoutWarningPayloadSchema);

const adminSessionPrepareSchema = baseMessageSchema
  .extend({
    type: z.literal(AdminMessageType.ADMIN_SESSION_PREPARE),
  })
  .merge(adminSessionPreparePayloadSchema);

const adminFetchConnectionHistorySchema = baseMessageSchema
  .extend({
    type: z.literal(AdminMessageType.ADMIN_FETCH_CONNECTION_HISTORY),
  })
  .merge(adminFetchConnectionHistoryPayloadSchema);

const adminConnectionHistoryResultSchema = baseMessageSchema
  .extend({
    type: z.literal(AdminMessageType.ADMIN_CONNECTION_HISTORY_RESULT),
  })
  .merge(adminConnectionHistoryResultPayloadSchema);

const adminLiveOpsSubscribeSchema = baseMessageSchema
  .extend({ type: z.literal(AdminMessageType.ADMIN_LIVEOPS_SUBSCRIBE) })
  .merge(adminLiveOpsSubscribePayloadSchema);

const adminLiveOpsUnsubscribeSchema = baseMessageSchema
  .extend({ type: z.literal(AdminMessageType.ADMIN_LIVEOPS_UNSUBSCRIBE) })
  .merge(adminLiveOpsUnsubscribePayloadSchema);

const adminLiveOpsSnapshotSchema = baseMessageSchema
  .extend({ type: z.literal(AdminMessageType.ADMIN_LIVEOPS_SNAPSHOT) })
  .merge(adminLiveOpsSnapshotPayloadSchema);

const adminLiveOpsDeltaSchema = baseMessageSchema
  .extend({ type: z.literal(AdminMessageType.ADMIN_LIVEOPS_DELTA) })
  .merge(adminLiveOpsDeltaPayloadSchema);

const adminScreenshotGrabRequestSchema = baseMessageSchema
  .extend({ type: z.literal(AdminMessageType.ADMIN_SCREENSHOT_GRAB_REQUEST) })
  .merge(adminScreenshotGrabRequestPayloadSchema);

const adminScreenshotGrabResultSchema = baseMessageSchema
  .extend({ type: z.literal(AdminMessageType.ADMIN_SCREENSHOT_GRAB_RESULT) })
  .merge(adminScreenshotGrabResultPayloadSchema);

const adminScreenshotGrabErrorSchema = baseMessageSchema
  .extend({ type: z.literal(AdminMessageType.ADMIN_SCREENSHOT_GRAB_ERROR) })
  .merge(adminScreenshotGrabErrorPayloadSchema);

const adminScheduledScreenshotGrabRequestSchema = baseMessageSchema
  .extend({ type: z.literal(AdminMessageType.ADMIN_SCHEDULED_SCREENSHOT_GRAB_REQUEST) })
  .merge(adminScreenshotGrabRequestPayloadSchema);

const adminScheduledScreenshotGrabResultSchema = baseMessageSchema
  .extend({ type: z.literal(AdminMessageType.ADMIN_SCHEDULED_SCREENSHOT_GRAB_RESULT) })
  .merge(adminScreenshotGrabResultPayloadSchema);

const adminScheduledScreenshotGrabErrorSchema = baseMessageSchema
  .extend({ type: z.literal(AdminMessageType.ADMIN_SCHEDULED_SCREENSHOT_GRAB_ERROR) })
  .merge(adminScreenshotGrabErrorPayloadSchema);

export const adminMessageSchema = z.discriminatedUnion('type', [
  adminActiveHostsResultSchema,
  adminOpenedSessionsResultSchema,
  adminFetchOpenedSessionsSchema,
  adminFetchActiveHostsSchema,
  adminFetchActiveHostsPaginatedSchema,
  adminActiveHostsPaginatedResultSchema,
  adminFetchSingleHostSchema,
  adminSingleHostResultSchema,
  adminFetchInvitationsPaginatedSchema,
  adminInvitationsPaginatedResultSchema,
  adminSubscribeActiveHostsSchema,
  adminKickPlayerSchema,
  adminKickPlayerResultSchema,
  adminFetchHostPlayersSchema,
  adminHostPlayersResultSchema,
  adminToggleHostMatchmakingSchema,
  adminToggleHostMatchmakingResultSchema,
  adminInvalidateHostSessionSchema,
  adminInvalidateHostSessionResultSchema,
  adminSwitchHostCampaignSchema,
  adminSwitchHostCampaignResultSchema,
  adminSessionRequestSchema,
  adminSessionApprovedSchema,
  adminSessionDeniedSchema,
  adminSessionEndSchema,
  adminSessionTimeoutWarningSchema,
  adminSessionPrepareSchema,
  adminFetchConnectionHistorySchema,
  adminConnectionHistoryResultSchema,
  adminLiveOpsSubscribeSchema,
  adminLiveOpsUnsubscribeSchema,
  adminLiveOpsSnapshotSchema,
  adminLiveOpsDeltaSchema,
  adminScreenshotGrabRequestSchema,
  adminScreenshotGrabResultSchema,
  adminScreenshotGrabErrorSchema,
  adminScheduledScreenshotGrabRequestSchema,
  adminScheduledScreenshotGrabResultSchema,
  adminScheduledScreenshotGrabErrorSchema,
]);

export type AdminMessage = z.infer<typeof adminMessageSchema>;

export function generateAdminMessage(
  type: AdminMessageType,
  payload: {
    data?: any[];
    sessions?: any[];
    invitations?: any[];
    records?: any[];
    machineId?: string;
    playerId?: string;
    kickType?: 'soft' | 'hard';
    reason?: string;
    success?: boolean;
    message?: string;
    error?: string;
    cursor?: string;
    nextCursor?: string | null;
    count?: number;
    hasMore?: boolean;
    players?: any[];
    host?: any;
    filters?: Record<string, unknown>;
    enabled?: boolean;
    timestamp?: number;
    campaignId?: string;
    sessionId?: string;
    hostBroadcastId?: string;
    playerPeerId?: string;
    reboot?: boolean;
    exitAction?: AdminSessionExitAction;
    cancelPendingApproval?: boolean;
    remainingSeconds?: number;
    force?: boolean;
    authoring?: MacroAuthoringSessionContext;
    ccu?: number;
    ccuHistory?: number[];
    matchmaking?: { started: number; succeeded: number; failed: number };
    successRateHistory?: number[];
    failureRateHistory?: number[];
    saturation?: { usedHosts: number; totalHosts: number };
    saturationHistory?: number[];
    funnel?: Record<string, number>;
    connectionTime?: { bins: Record<string, number>; p50: number; p95: number };
    errors?: Record<string, number>;
    changes?: Record<string, unknown>;
    imageUrl?: string;
    sizeBytes?: number;
    expiresAt?: number;
  },
): AdminMessage {
  const base = {
    id: createMessageId(),
    timestamp: Date.now(),
    type,
  };
  switch (type) {
    case AdminMessageType.ADMIN_ACTIVE_HOSTS_RESULT:
      return adminActiveHostsResultSchema.parse({ ...base, ...payload });
    case AdminMessageType.ADMIN_OPENED_SESSIONS_RESULT:
      return adminOpenedSessionsResultSchema.parse({ ...base, ...payload });
    case AdminMessageType.ADMIN_FETCH_OPENED_SESSIONS:
      return adminFetchOpenedSessionsSchema.parse({ ...base, ...payload });
    case AdminMessageType.ADMIN_FETCH_ONLINE_HOSTS:
      return adminFetchActiveHostsSchema.parse({ ...base, ...payload });
    case AdminMessageType.ADMIN_FETCH_ONLINE_HOSTS_PAGINATED:
      return adminFetchActiveHostsPaginatedSchema.parse({ ...base, ...payload });
    case AdminMessageType.ADMIN_ACTIVE_HOSTS_PAGINATED_RESULT:
      return adminActiveHostsPaginatedResultSchema.parse({ ...base, ...payload });
    case AdminMessageType.ADMIN_FETCH_SINGLE_HOST:
      return adminFetchSingleHostSchema.parse({ ...base, ...payload });
    case AdminMessageType.ADMIN_SINGLE_HOST_RESULT:
      return adminSingleHostResultSchema.parse({ ...base, ...payload });
    case AdminMessageType.ADMIN_FETCH_INVITATIONS_PAGINATED:
      return adminFetchInvitationsPaginatedSchema.parse({ ...base, ...payload });
    case AdminMessageType.ADMIN_INVITATIONS_PAGINATED_RESULT:
      return adminInvitationsPaginatedResultSchema.parse({ ...base, ...payload });
    case AdminMessageType.ADMIN_SUBSCRIBE_ONLINE_HOSTS:
      return adminSubscribeActiveHostsSchema.parse({ ...base, ...payload });
    case AdminMessageType.ADMIN_KICK_PLAYER:
      return adminKickPlayerSchema.parse({ ...base, ...payload });
    case AdminMessageType.ADMIN_KICK_PLAYER_RESULT:
      return adminKickPlayerResultSchema.parse({ ...base, ...payload });
    case AdminMessageType.ADMIN_FETCH_HOST_PLAYERS:
      return adminFetchHostPlayersSchema.parse({ ...base, ...payload });
    case AdminMessageType.ADMIN_HOST_PLAYERS_RESULT:
      return adminHostPlayersResultSchema.parse({ ...base, ...payload });
    case AdminMessageType.ADMIN_TOGGLE_HOST_MATCHMAKING:
      return adminToggleHostMatchmakingSchema.parse({ ...base, ...payload });
    case AdminMessageType.ADMIN_TOGGLE_HOST_MATCHMAKING_RESULT:
      return adminToggleHostMatchmakingResultSchema.parse({ ...base, ...payload });
    case AdminMessageType.ADMIN_INVALIDATE_HOST_SESSION:
      return adminInvalidateHostSessionSchema.parse({ ...base, ...payload });
    case AdminMessageType.ADMIN_INVALIDATE_HOST_SESSION_RESULT:
      return adminInvalidateHostSessionResultSchema.parse({ ...base, ...payload });
    case AdminMessageType.ADMIN_SWITCH_HOST_CAMPAIGN:
      return adminSwitchHostCampaignSchema.parse({ ...base, ...payload });
    case AdminMessageType.ADMIN_SWITCH_HOST_CAMPAIGN_RESULT:
      return adminSwitchHostCampaignResultSchema.parse({ ...base, ...payload });
    case AdminMessageType.ADMIN_SESSION_REQUEST:
      return adminSessionRequestSchema.parse({ ...base, ...payload });
    case AdminMessageType.ADMIN_SESSION_APPROVED:
      return adminSessionApprovedSchema.parse({ ...base, ...payload });
    case AdminMessageType.ADMIN_SESSION_DENIED:
      return adminSessionDeniedSchema.parse({ ...base, ...payload });
    case AdminMessageType.ADMIN_SESSION_END:
      return adminSessionEndSchema.parse({ ...base, ...payload });
    case AdminMessageType.ADMIN_SESSION_TIMEOUT_WARNING:
      return adminSessionTimeoutWarningSchema.parse({ ...base, ...payload });
    case AdminMessageType.ADMIN_SESSION_PREPARE:
      return adminSessionPrepareSchema.parse({ ...base, ...payload });
    case AdminMessageType.ADMIN_FETCH_CONNECTION_HISTORY:
      return adminFetchConnectionHistorySchema.parse({ ...base, ...payload });
    case AdminMessageType.ADMIN_CONNECTION_HISTORY_RESULT:
      return adminConnectionHistoryResultSchema.parse({ ...base, ...payload });
    case AdminMessageType.ADMIN_LIVEOPS_SUBSCRIBE:
      return adminLiveOpsSubscribeSchema.parse({ ...base, ...payload });
    case AdminMessageType.ADMIN_LIVEOPS_UNSUBSCRIBE:
      return adminLiveOpsUnsubscribeSchema.parse({ ...base, ...payload });
    case AdminMessageType.ADMIN_LIVEOPS_SNAPSHOT:
      return adminLiveOpsSnapshotSchema.parse({ ...base, ...payload });
    case AdminMessageType.ADMIN_LIVEOPS_DELTA:
      return adminLiveOpsDeltaSchema.parse({ ...base, ...payload });
    case AdminMessageType.ADMIN_SCREENSHOT_GRAB_REQUEST:
      return adminScreenshotGrabRequestSchema.parse({ ...base, ...payload });
    case AdminMessageType.ADMIN_SCREENSHOT_GRAB_RESULT:
      return adminScreenshotGrabResultSchema.parse({ ...base, ...payload });
    case AdminMessageType.ADMIN_SCREENSHOT_GRAB_ERROR:
      return adminScreenshotGrabErrorSchema.parse({ ...base, ...payload });
    case AdminMessageType.ADMIN_SCHEDULED_SCREENSHOT_GRAB_REQUEST:
      return adminScheduledScreenshotGrabRequestSchema.parse({ ...base, ...payload });
    case AdminMessageType.ADMIN_SCHEDULED_SCREENSHOT_GRAB_RESULT:
      return adminScheduledScreenshotGrabResultSchema.parse({ ...base, ...payload });
    case AdminMessageType.ADMIN_SCHEDULED_SCREENSHOT_GRAB_ERROR:
      return adminScheduledScreenshotGrabErrorSchema.parse({ ...base, ...payload });
    default: {
      const exhaustiveCheck: never = type;
      throw new Error(`Unhandled admin message type: ${exhaustiveCheck}`);
    }
  }
}

